import React, { FC } from "react";
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Typography,
} from "@mui/material";
import FeatherIcon from "feather-icons-react";
import { useTranslation } from "react-i18next";

import { ColumnDefinition } from "../../EmployeeDataDrawer";
import { SALARY_GROUPS } from "../../helpers";

import { Button } from "@components/base";

export const HIDEABLE_COLUMNS = [
  "yearsOfService",
  "educationAndTraining",
  "employeeScarcity",
  "employeeEvaluation",
  "category",
  "levelOfRequirements2",
  "levelOfRequirements3",
  "professionalPosition",
  "wageCompensationForVacation",
  "wageCompensationDoctorFamilyLeave",
  "sickLeaveIncomeCompensation",
  "overtimeWage",
  "overtimeAllowance",
  "riskAllowance",
  "standbyDuty",
  "monthlyBonus",
  "standardBonus",
  "specialBonus",
  "fiftyFivePlusBonus",
  "retirementBenefit",
  "severancePay",
  "supplementaryPensionInsurance",
  "lifeInsurance",
  "mealVouchers",
  "cafeteria",
  "carAllowance",
  "employeeContribution",
  "remarks",
  "additionalInformation01",
  "additionalInformation02",
  "additionalInformation03",
  "additionalInformation04",
  "additionalInformation05",
] as const;

export type HideableColumn = (typeof HIDEABLE_COLUMNS)[number];

interface ColumnSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  columns: ColumnDefinition[];
  hiddenColumns: Set<string>;
  onToggleColumn: (columnKey: string) => void;
  onShowAll: () => void;
  onHideAll: () => void;
}

export const ColumnSettingsDialog: FC<ColumnSettingsDialogProps> = ({
  open,
  onClose,
  columns,
  hiddenColumns,
  onToggleColumn,
  onShowAll,
  onHideAll,
}) => {
  const { t } = useTranslation();

  const hideableSet = new Set<string>(HIDEABLE_COLUMNS);

  const ungroupedCols = columns.filter(
    (col) => !col.group && hideableSet.has(col.key),
  );

  const renderCheckbox = (col: ColumnDefinition): React.ReactNode => (
    <FormControlLabel
      key={col.key}
      control={
        <Checkbox
          checked={!hiddenColumns.has(col.key)}
          onChange={() => onToggleColumn(col.key)}
          size="small"
        />
      }
      label={
        <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
          {col.label}
        </Typography>
      }
      sx={{
        m: 0,
        py: 0.5,
        px: 1,
        borderRadius: 1,
        "&:hover": { bgcolor: "grey.50" },
      }}
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {t("paygap.employeeData.columnSettings.title", "Column Settings")}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <FeatherIcon icon="x" size={20} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {t(
            "paygap.employeeData.columnSettings.description",
            "Select which columns to show in the employee data table. Some columns are always visible.",
          )}
        </Typography>

        <Box display="flex" gap={1} mb={2}>
          <Button variant="outlined" size="small" onClick={onShowAll}>
            {t("paygap.employeeData.columnSettings.showAll", "Show all")}
          </Button>
          <Button variant="outlined" size="small" onClick={onHideAll}>
            {t("paygap.employeeData.columnSettings.hideAll", "Hide all")}
          </Button>
        </Box>

        {/* Ungrouped columns */}
        {ungroupedCols.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
              {t("paygap.employeeData.columnSettings.general", "General")}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 1,
                mb: 2,
              }}
            >
              {ungroupedCols.map(renderCheckbox)}
            </Box>
          </>
        )}

        {/* Grouped columns by salary group */}
        {SALARY_GROUPS.map((group) => {
          const groupCols = columns.filter(
            (col) => col.group === group.id && hideableSet.has(col.key),
          );

          if (groupCols.length === 0) return null;

          return (
            <React.Fragment key={group.id}>
              <Divider sx={{ my: 1 }} />
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, mt: 1, fontWeight: "bold" }}
              >
                {t(group.labelKey, group.defaultLabel)}
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 1,
                  mb: 1,
                }}
              >
                {groupCols.map(renderCheckbox)}
              </Box>
            </React.Fragment>
          );
        })}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="contained" onClick={onClose}>
          {t("common.done", "Done")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
